// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

// components
import {
  BaseField,
  Button,
  Checkbox,
  CheckboxSize,
  CheckboxVariant,
  FieldProps,
  Input,
  Option,
} from '@/components/atoms';
import { CardContainer } from '@/components/templates';

/**
 * MultiSelectBox allows the user to select multiple values from a list of
 * options in the form of chips.
 */
export const MultiSelectBox = ({
  className,
  id,
  ariaLabel,
  label,
  description,
  tooltip,
  isRequired,
  options,
  value,
  maxColumns,
  size,
  onChange,
  isCustom,
  validate,
  variant = 'box',
  isDisabled = false,
  isHideTitle = false,
}: MultiSelectBoxProps) => {
  // state
  const [customValue, setCustomValue] = useState('');
  const [isAddAnother, setIsAddAnother] = useState(false);

  // memo
  const valueArray = useMemo(() => JSON.parse(value || '[]'), [value]);

  const customValues = useMemo(() => {
    return valueArray.filter(
      (val: string) =>
        !options.map((option: Option) => option.value).includes(val),
    );
  }, [valueArray, options]);

  const sortedOptions = useMemo(
    () => options.sort((a, b) => (a?.order || 0) - (b?.order || 0)),
    [options],
  );

  // methods
  const handleBoxPress = useCallback(
    (value: string) => {
      const index = valueArray.indexOf(value);
      if (index === -1) {
        onChange?.(JSON.stringify([...valueArray, value]));
      } else {
        const newValue = [...valueArray];
        newValue.splice(index, 1);
        onChange?.(JSON.stringify(newValue));
      }
    },
    [valueArray, onChange],
  );

  const handleSaveCustomValue = useCallback(() => {
    if (customValue.trim() !== '' && !valueArray.includes(customValue)) {
      onChange?.(JSON.stringify([...valueArray, customValue]));
    }

    setIsAddAnother(false);
    setCustomValue('');
  }, [valueArray, customValue, onChange, options]);

  // effects
  useEffect(() => {
    const inputField = document.getElementById('addAnotherInput');
    if (inputField && isAddAnother) {
      const inputY = inputField.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: inputY, behavior: 'smooth' });
    }
  }, [isAddAnother]);

  return (
    <BaseField
      id={id}
      label={label}
      ariaLabel={ariaLabel}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Wrapper className={className}>
        {sortedOptions.length + customValues.length > 0 && (
          <CardContainer
            maxColumns={maxColumns ?? 2}
            size='sm'
            isPreventHeightMatch
          >
            {sortedOptions.map((option) => (
              <Checkbox
                key={option.value}
                id={option.value}
                label={option.label}
                variant={variant}
                size={size ?? 'sm'}
                value={valueArray.includes(option.value)}
                onChange={() => handleBoxPress(option.value)}
                isDisabled={isDisabled}
                icon={option.icon}
                iconFill={option.iconFill}
              />
            ))}
            {customValues.map((customValue: string) => (
              <Checkbox
                key={customValue}
                id={`${id}-${customValue}`}
                label={customValue}
                variant={variant}
                size={size ?? 'sm'}
                value={valueArray.includes(customValue)}
                onChange={() => handleBoxPress(customValue)}
                isDisabled={isDisabled}
              />
            ))}
          </CardContainer>
        )}
        {isCustom && (
          <AddAnotherWrapper>
            {!isAddAnother ? (
              <Button
                ariaLabel='add another'
                iconLeft={'plus'}
                variant='secondary'
                onPress={() => setIsAddAnother(true)}
                isDisabled={isDisabled}
              >
                Add another
              </Button>
            ) : (
              <>
                <Input
                  id='addAnotherInput'
                  ariaLabel='something else'
                  placeholder='Something else'
                  value={customValue}
                  onChange={(val) => setCustomValue(val || '')}
                  isDisabled={isDisabled}
                  validate={validate}
                  autoFocus
                />
                <ActionWrapper>
                  <Button
                    ariaLabel='save'
                    variant='secondary'
                    size='sm'
                    type='submit'
                    onPress={handleSaveCustomValue}
                    isDisabled={
                      isDisabled || (validate && !validate(customValue))
                    }
                  >
                    Save
                  </Button>
                  <Button
                    ariaLabel='cancel'
                    variant='link'
                    size='sm'
                    onPress={() => {
                      setIsAddAnother(false);
                      setCustomValue('');
                    }}
                  >
                    Cancel
                  </Button>
                </ActionWrapper>
              </>
            )}
          </AddAnotherWrapper>
        )}
      </Wrapper>
    </BaseField>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

const AddAnotherWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

const ActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
`;

export interface MultiSelectBoxProps extends FieldProps<string> {
  className?: string;
  maxColumns?: number;
  size?: CheckboxSize;
  options: Option[];
  isCustom?: boolean;
  variant?: CheckboxVariant;
}

MultiSelectBox.displayName = 'MultiSelectBox';

export default MultiSelectBox;
